import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { blogPosts } from '@/mocks/blog';
import SeoHead from '@/components/SeoHead';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

export default function BlogPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', ...Array.from(new Set(blogPosts.map(p => lang === 'ar' ? p.categoryAr : p.category)))];

  const filteredPosts = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter(p => (lang === 'ar' ? p.categoryAr : p.category) === activeCategory);

  const isAr = lang === 'ar';

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={isAr
          ? 'مدونة Debug - مقالات وموارد تطوير البرمجيات'
          : 'Debug Blog - Software Development Insights & Resources'}
        description={isAr
          ? 'اقرأ أحدث المقالات حول تصحيح البرمجيات، التطوير المخصص، الخدمات السحابية، وأتمتة مكان العمل. دليل الخبراء للشركات المصرية.'
          : 'Read the latest articles on software debugging, custom development, cloud services, and workplace automation. Expert insights for Egyptian businesses.'}
        keywords="software debugging blog, custom development articles, cloud migration guide, workplace automation tips, software development Egypt"
        canonical="https://debuging.com/blog"
        lang={lang}
      />
      <header>
        <Navbar />
      </header>

      <main className="pt-28 pb-20">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="text-center">
            <div className="inline-flex items-center bg-[rgb(0,0,139)]/10 rounded-full px-4 py-2 mb-4">
              <span className="text-[rgb(0,0,139)] text-sm font-semibold">
                {isAr ? 'المدونة' : 'Blog'}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {isAr ? 'رؤى وموارد تطوير البرمجيات' : 'Software Development Insights'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {isAr
                ? 'مقالات خبيرة حول تصحيح البرمجيات، التطوير المخصص، والخدمات السحابية للشركات المصرية.'
                : 'Expert articles on software debugging, custom development, and cloud services for Egyptian businesses.'}
            </p>
          </div>
        </section>

        {/* Category Filters */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[rgb(0,0,139)] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* Blog Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article
                key={post.slug}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
                itemScope
                itemType="https://schema.org/BlogPosting"
              >
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={isAr ? post.titleAr : post.title}
                      title={isAr ? post.titleAr : post.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      itemProp="image"
                      loading="lazy"
                    />
                  </div>
                </Link>
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-3 mb-3 text-sm text-gray-500">
                    <span className="bg-[rgb(0,0,139)]/10 text-[rgb(0,0,139)] px-3 py-1 rounded-full font-medium">
                      {isAr ? post.categoryAr : post.category}
                    </span>
                    <span>{post.date}</span>
                    <span>{isAr ? post.readTimeAr : post.readTime}</span>
                  </div>
                  <h2
                    className="text-xl font-bold text-gray-900 mb-3 leading-tight hover:text-[rgb(0,0,139)] transition-colors"
                    itemProp="headline"
                  >
                    <Link to={`/blog/${post.slug}`}>
                      {isAr ? post.titleAr : post.title}
                    </Link>
                  </h2>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-grow" itemProp="description">
                    {isAr ? post.excerptAr : post.excerpt}
                  </p>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center text-[rgb(0,0,139)] font-semibold text-sm hover:underline cursor-pointer"
                  >
                    {isAr ? 'اقرأ المزيد' : 'Read more'}
                    <i className={isAr ? 'ri-arrow-left-line ml-1' : 'ri-arrow-right-line ml-1'}></i>
                  </Link>
                </div>
                <meta itemProp="datePublished" content={post.date} />
                <meta itemProp="author" content="Debug" />
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer>
        <Footer />
      </footer>
    </div>
  );
}